/**
 * useArTableBundleRows
 *
 * The memo boundary in front of `resolveArBundleDisplay`, and nothing else — it
 * owns no rule of its own. What renders, why present members are unpainted, how
 * many members a bundle really has and whether it can receive a drop are all
 * answered in `bundleDisplay/resolveArBundleDisplay`; this hook only holds the
 * result's identity steady for `ArTableBody`, whose grid re-renders when an
 * input moves and not otherwise. `null` means the page holds no bundles and the
 * caller takes the flat render path.
 */

import { useMemo } from 'react';
import type { Snapshot } from 'valtio';
import type { ArRowData, ArRowId } from '#types/System.Artifacts.types';
import type { DocEntry } from '../../store/Store.types';
import { resolveArBundleDisplay } from './bundleDisplay';
import type { ArBundleDisplay, ArTableRowDragState } from './bundleDisplay';

type UseArTableBundleRowsParams = {
  visibleRowIds: ArRowId[];
  collapsedBundles: Readonly<Record<string, true>>;
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>> | null;
  hideKitMembers: boolean;
  hideGroupMembers: boolean;
  drag: ArTableRowDragState;
  /** Whole-page member census, so a narrowed bundle still reports its true size. */
  memberCountByBundleId: ReadonlyMap<string, number>;
  /**
   * Header documents of bundles cut by a page boundary, keyed by bundleId
   * (SYD-2430). Snapshots, not proxies: the resolver reads them inside the memo
   * below, where a bare proxy would never subscribe.
   */
  offPageHeaders?: ReadonlyMap<string, Snapshot<ArRowData>>;
  /** The bundle a page-turn probe proved continues past this page's last row. */
  continuingBundleId?: string;
};

function useArTableBundleRows({
  visibleRowIds,
  collapsedBundles,
  rowsDocs,
  hideKitMembers,
  hideGroupMembers,
  drag,
  memberCountByBundleId,
  offPageHeaders,
  continuingBundleId,
}: UseArTableBundleRowsParams): ArBundleDisplay | null {
  return useMemo(() => resolveArBundleDisplay({
    rowIds: visibleRowIds,
    rowsDocs,
    collapsedBundles,
    hideKitMembers,
    hideGroupMembers,
    drag,
    memberCountByBundleId,
    offPageHeaders,
    continuingBundleId,
  }), [
    collapsedBundles,
    continuingBundleId,
    drag,
    hideGroupMembers,
    hideKitMembers,
    memberCountByBundleId,
    offPageHeaders,
    rowsDocs,
    visibleRowIds,
  ]);
}

export { useArTableBundleRows };
